/*
 * Clare cart purchase options.
 *
 * Cart lines show the real bag count - the same number checkout shows - so
 * nothing is translated between the two. The plan's bags-per-delivery is used
 * as the stepper's increment instead, which keeps a 3-month line on 3 / 6 / 9
 * rather than letting it hold an invalid 4.
 *
 * The line separates two decisions: a toggle for whether to subscribe, and a
 * select for how often. Both end in the same request - Shopify's cart API takes
 * `selling_plan` alongside `line` and `quantity`, with null clearing the plan.
 */
(function () {
  if (window.__clareCartPurchaseOptions) return;
  window.__clareCartPurchaseOptions = true;

  var UPDATING_CLASS = "cart-items__table-row--updating";

  /* Any change here is a round trip to Shopify, so the row is greyed out and
     given a spinner until the cart re-renders. That re-render replaces the row
     and takes the class with it; the timeout is only a backstop so a request
     that never completes can't leave a row permanently dimmed and dead. */
  function markUpdating(row) {
    if (!row) return;
    row.classList.add(UPDATING_CLASS);
    window.setTimeout(function () {
      row.classList.remove(UPDATING_CLASS);
    }, 12000);
  }

  /*
   * The composite key leans on the line index, which only holds while the cart
   * keeps the same number of lines. Clearing a plan can make a line identical
   * to an existing one-time line of the same variant, in which case Shopify
   * merges them and every index below shifts. Rather than risk restoring one
   * line's plan onto another, drop the memory whenever the line count moves.
   * Falling back to the default is safe; restoring the wrong plan is not.
   */
  var lastLineCount = null;

  function forgetIfCartShapeChanged() {
    var count = document.querySelectorAll("[data-cart-subscribe]").length;
    if (lastLineCount !== null && count !== lastLineCount) {
      try {
        sessionStorage.removeItem(MEMORY_KEY);
      } catch (e) {
        /* nothing to clear */
      }
    }
    lastLineCount = count;
  }

  document.addEventListener("cart:update", function () {
    document.querySelectorAll("." + UPDATING_CLASS).forEach(function (row) {
      row.classList.remove(UPDATING_CLASS);
    });

    /* The cart component dispatches this event BEFORE morphSection swaps the
       rows in, so anything read or written here sees the outgoing markup. That
       made the line count permanently stale - it never observed a line being
       added - so the next toggle looked like a shape change and threw away the
       memory that had just been written. Defer to the next task so both run
       against the DOM that actually survives. */
    setTimeout(function () {
      forgetIfCartShapeChanged();
      restoreSelects();
    }, 0);
  });

  /*
   * Clearing a line's selling plan destroys the information needed to restore
   * it. A 3-month line holding 3 bags is one delivery of a 3-pack, but once the
   * plan is gone the cart just sees "3 bags" - pack size is unknowable, so
   * line_units re-derives as 3 rather than 1. Toggling back on then multiplies
   * by that, producing 3 bags of the monthly plan (or 9 if the 3-month option
   * were still selected).
   *
   * So the shopper's choice is held here across the off/on cycle rather than
   * re-derived from a line that no longer carries it. sessionStorage because
   * the row is replaced wholesale by the cart re-render, taking any JS state on
   * it with it.
   *
   * Keyed by variant id AND line index together, which is not arbitrary:
   *
   *  - Not the cart line key. Shopify documents those as "not stable
   *    identifiers" that change as the line's characteristics change, and
   *    clearing the selling plan is exactly such a change - the memory would be
   *    written under one key and read back under another.
   *  - Not the variant id alone. The same variant with a different selling plan
   *    is a *different* cart line, so a single-product store can easily hold a
   *    Monthly line, a 3-Month line and a one-time line at once. They would all
   *    collide on one slot and restore each other's plan.
   *  - Not the line index alone. It shifts when a line above is removed.
   *
   * Together they are stable across the off/on round trip (which adds and
   * removes nothing) while still telling same-variant lines apart.
   */
  var MEMORY_KEY = "clare:purchase-options";

  function memoryKey(root) {
    return (root.dataset.variantId || "v") + ":" + (root.dataset.line || "0");
  }

  function readMemory() {
    try {
      return JSON.parse(sessionStorage.getItem(MEMORY_KEY)) || {};
    } catch (e) {
      return {}; // private mode or storage disabled - the widget still works
    }
  }

  function writeMemory(memory) {
    try {
      sessionStorage.setItem(MEMORY_KEY, JSON.stringify(memory));
    } catch (e) {
      /* nothing to do: without memory the toggle simply forgets, as before */
    }
  }

  function remember(line, plan, units) {
    var memory = readMemory();
    memory[line] = { plan: plan, units: units };
    writeMemory(memory);
  }

  function recall(line) {
    return readMemory()[line] || null;
  }

  function forget(line) {
    var memory = readMemory();
    delete memory[line];
    writeMemory(memory);
  }

  function optionByValue(select, value) {
    if (!select) return null;
    for (var i = 0; i < select.options.length; i++) {
      if (select.options[i].value === value) return select.options[i];
    }
    return null;
  }

  /* After a re-render an unsubscribed line has no plan, so Liquid falls back to
     the first option. Put the shopper's actual choice back on screen. */
  function restoreSelects() {
    document.querySelectorAll("[data-cart-subscribe]").forEach(function (root) {
      var toggle = root.querySelector("[data-cart-subscribe-toggle]");
      var select = root.querySelector("[data-cart-plan-select]");
      if (!toggle || !select || toggle.checked) return;

      var memo = recall(memoryKey(root));
      if (memo && memo.plan && optionByValue(select, memo.plan)) {
        select.value = memo.plan;
      }
    });
  }

  function packSizeFor(el) {
    var wrap = el && el.closest ? el.closest("[data-cart-pack-size]") : null;
    if (!wrap) return 1;
    var size = parseInt(wrap.dataset.cartPackSize, 10);
    return size > 0 ? size : 1;
  }

  /*
   * The stepper counts deliveries; Shopify counts bags. This converts between
   * the two.
   *
   * Capture phase, so the multiplied value lands before the cart component's
   * own bubble-phase listener reads event.detail.quantity and sends it. The
   * input's own value is deliberately left alone: it shows deliveries, which is
   * what the shopper chose and what the rest of the line is labelled in.
   */
  document.addEventListener(
    "quantity-selector:update",
    function (event) {
      if (!event.detail) return;

      var packSize = packSizeFor(event.target);
      if (packSize <= 1) return;

      var units = Number(event.detail.quantity);
      if (!isFinite(units) || units <= 0) return; // 0 removes the line

      event.detail.quantity = units * packSize;
    },
    true
  );

  /*
   * The cart form posts bags; the visible stepper counts deliveries, so it
   * carries no form name and a hidden updates[] field holds the real quantity.
   * That hidden value is written by Liquid and only refreshed when the section
   * re-renders, so a value typed but not yet committed -- someone typing 2 and
   * pressing Enter, which submits before the input's blur commits it -- would
   * otherwise be thrown away and the old quantity posted back.
   *
   * Capture phase, so the fields are correct before anything serialises them.
   */
  document.addEventListener(
    "submit",
    function (event) {
      var form = event.target;
      if (!form || form.id !== "cart-form") return;

      form.querySelectorAll("[data-cart-pack-size]").forEach(function (wrap) {
        var packSize = parseInt(wrap.dataset.cartPackSize, 10);
        if (!(packSize > 0)) packSize = 1;

        var visible = wrap.querySelector('input[type="number"]');
        var hidden = wrap.querySelector('input[type="hidden"][name="updates[]"]');
        if (!visible || !hidden) return;

        var units = parseInt(visible.value, 10);
        if (!isFinite(units) || units < 0) return;

        hidden.value = String(units * packSize);
      });
    },
    true
  );

  /**
   * Sends the line's new purchase option to Shopify.
   * @param {Element} root - the [data-cart-subscribe] block for this line
   * @param {string|null} sellingPlanId - null (or "") for a one-time purchase
   * @param {number} packSize - bags per delivery for the chosen option
   */
  function applyOption(root, sellingPlanId, packSize, unitsOverride) {
    var cartItems = root.closest("cart-items-component");
    if (!cartItems || typeof cartItems.updateQuantity !== "function") return;

    var line = parseInt(root.dataset.line, 10);
    if (!(line > 0)) return;

    /* Deliveries' worth is preserved across a change, not the raw bag count:
       one delivery of the 3-month plan (3 bags) becomes one of monthly (1 bag).
       The override carries a remembered value when the line's own units can no
       longer be trusted - see the memory helpers above. */
    var units = parseInt(
      unitsOverride !== undefined && unitsOverride !== null ? unitsOverride : root.dataset.units,
      10
    );
    if (!(units > 0)) units = 1;
    if (!(packSize > 0)) packSize = 1;

    markUpdating(root.closest(".cart-items__table-row"));

    cartItems.updateQuantity({
      line: line,
      quantity: units * packSize,
      selling_plan: sellingPlanId ? sellingPlanId : null,
      action: "change",
    });
  }

  function selectedOption(select) {
    return select ? select.options[select.selectedIndex] : null;
  }

  document.addEventListener("change", function (event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var root = target.closest("[data-cart-subscribe]");
    if (!root) return;

    var select = root.querySelector("[data-cart-plan-select]");

    // How often.
    if (target.matches("[data-cart-plan-select]")) {
      var option = selectedOption(select);
      if (!option) return;
      /* An explicit frequency change supersedes anything remembered. */
      forget(memoryKey(root));
      select.disabled = true;
      applyOption(root, option.value, parseInt(option.dataset.packSize, 10));
      return;
    }

    // Whether to subscribe.
    if (target.matches("[data-cart-subscribe-toggle]")) {
      var on = target.checked;

      /* Reveal or hide straight away rather than waiting for the round trip,
         so the toggle feels immediate. The re-render then replaces the row. */
      root.querySelectorAll("[data-cart-subscribe-reveal]").forEach(function (el) {
        el.classList.toggle("hidden", !on);
      });
      target.disabled = true;

      if (!on) {
        /* Recorded before the plan is cleared, while the line still knows its
           pack size and therefore its true delivery count. */
        remember(memoryKey(root), select ? select.value : "", root.dataset.units);
        /* Keep the volume the shopper had. Dropping a 3-bag delivery to a
           single bag silently shrinks their order; buying once at the same
           volume is what the PDP's "Buy Once" does too. Passed as units with a
           pack size of 1, so quantity comes out as the bag count unchanged. */
        applyOption(root, null, 1, root.dataset.bags);
        return;
      }

      /* Prefer the remembered choice over whatever Liquid defaulted the select
         to once the line lost its plan. */
      var memo = recall(memoryKey(root));
      var chosen = selectedOption(select);
      if (memo && memo.plan) {
        var remembered = optionByValue(select, memo.plan);
        if (remembered) chosen = remembered;
      }
      forget(memoryKey(root));

      applyOption(
        root,
        chosen ? chosen.value : null,
        chosen ? parseInt(chosen.dataset.packSize, 10) : 1,
        memo ? memo.units : null
      );
    }
  });

  /* Placed last on purpose: initMemory() runs synchronously, and this script is
     deferred - so readyState is never "loading" and the else branch always
     fires. Sitting above `var MEMORY_KEY` it read sessionStorage under an
     undefined key and the first-paint restore silently did nothing. */
  function initMemory() {
    lastLineCount = document.querySelectorAll("[data-cart-subscribe]").length;
    restoreSelects();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initMemory);
  } else {
    initMemory();
  }
})();
